/**
 * DeviceContextMenuMixin JS
 *
 * 장비 관련 기능 컨텍스트 메뉴 믹스인
 *
 * @since 2021-02-02
 * @author mk.ko
 */

import { createNamespacedHelpers } from 'vuex'

const
  commonHelper = createNamespacedHelpers('common/common'),
  navigationHelpers = createNamespacedHelpers('common/navigation'),
  localeHelper = createNamespacedHelpers('common/locale'),
  authenticateHelper = createNamespacedHelpers('common/authenticate'),
  deviceTreeHelpers = createNamespacedHelpers('common/devicetree'),
  deviceContextMenuHelpers = createNamespacedHelpers('common/deviceContextMenu')

import { newWindow } from '~/utils/newWindow'
import { menuCodeToHierarchyInfo } from '~/utils/multirw'
import { isDeviceGroupType } from '~/utils/common'
import { checkIpv6 } from '~/utils/ip'

export default {
  computed: {
    ...commonHelper.mapState([
      'agentTime',
      'newWindows'
    ]),
    ...navigationHelpers.mapState([
      'menu'
    ]),
    ...localeHelper.mapGetters([
      'getCurrentLocale'
    ]),
    ...authenticateHelper.mapState([
      'authToken',
      'authIssueDate',
      'authRefreshToken'
    ]),
    alertDialog () {
      return this.$store.state.common.common.components.alertDialog
    }
  },
  data () {
    return {}
  },
  async mounted () {},
  beforeDestroy () {},
  methods: {
    ...deviceTreeHelpers.mapActions([
      'getLoginDeviceInfo',
      'getLoginDeviceConnectCheck',
      'getLoginKey',
      'getDeviceConnectInfo'
    ]),
    ...deviceContextMenuHelpers.mapActions([
      'checkDeviceConnection'
    ]),
    // NGF 장비 Side GUI 버튼 클릭(장비 및 그룹 편집에서 사용)
    async onGUIExec (selectedData = {}, menuKey = '') {
      if (_.isEmpty(selectedData)) return

      this.$loader.show()

      try {
        const deviceId = Number(selectedData.device_id)
        let res = await this.getDeviceConnectInfo(deviceId)
        if (res.code !== 'ok') {
          this.alertDialog.show(res.result.msg, 'error')
          this.$loader.hide()
          return
        }
        const connectInfo = res.result
        const devicePort = connectInfo.redirect_port === 443 ? '' : `:${connectInfo.redirect_port}`
        let address = connectInfo.redirect_ip

        // Check IPv6
        if (checkIpv6(address)) {
          address = `[${address}]`
        }

        // NGF/IPS - onekey 발급 요청
        res = await this.getLoginKey({
          ip: address,
          port: devicePort,
          clientId: connectInfo.device_gui_client_val,
          clientSecret: connectInfo.device_gui_secret_val
        })

        let url = `https://${address}${devicePort}`

        if (res.code === 'ok') {
          const loginInfo = res.result
          url += `?onekey=${loginInfo.onekey}`

          // TAMS
          if (_.get(menuKey, ['length'], 0) > 0) {
            // NGIPS 1.0 만 현재 지원됨, (ex. '/uan/an-security/anp-threat'), newWindows 사용법
            url += `&menu_key=${encodeURIComponent(menuKey)}`
          }
        }

        newWindow({
          url: url,
          sameOrigin: false
        })
      } catch (e) {
        this.$log('[Device Tree Error (onGUI)]: ', e)
      }
      this.$loader.hide()
    },
    async onNetworkToolsExec ({ menuCode, selectedData = {} } = {}) {
      if (_.isEmpty(selectedData) || !menuCode) {
        return
      }

      this.$loader.show()

      try {
        const res = await this.getLoginDeviceInfo(Number(selectedData.device_id)) // GUI 실행 대상 장비 정보 로드

        if (res.code === 'ok') {
          const deviceInfo = res.result[0]
          const menuInfo = menuCodeToHierarchyInfo(menuCode, this.menu)
          let name = '', menuName = ''

          name = _.map(menuInfo, 'key').join('-')
          menuName = menuInfo[menuInfo.length - 1].name

          const routeData = this.$router.resolve({
            name: name,
            query: {
              menuName: menuName,
              newWindow: true,
              deviceTime: this.agentTime,
              target: deviceInfo.device_ip
            }
          })

          newWindow({
            store: this.$store,
            url: routeData.href,
            props: {
              width: 1400,
              height: 620
            }
          })
        } else {
          if (_.get(res, ['result', 'msg', 'length'], 0) > 0) {
            const globalAlertDialog = this.$store.state.common.common.components.alertDialog
            globalAlertDialog.show(res.result.msg, 'error')
          }
        }
      } catch (e) {
        this.$log('[Device Context Menu GUI Error (onNetworkToolsExec)]', e)
      }
      this.$loader.hide()
    },
    isDeviceGroupType (deviceType) {
      try {
        return isDeviceGroupType(deviceType)
      } catch (e) {
        this.$log('[Device Context Menu GUI Error (isDeviceGroupType)]', e)
      }
    },
    async checkConnect ({ ip, port = 443 } = {}) {
      if (_.get(ip, ['length'], 0) === 0) {
        return
      }
      try {
        const res = await this.checkDeviceConnection({ip, port})
        if (res.code === 'ok' && res.result.msg === 'ok') {
          this.alertDialog.show(this.$t('sm.deviceConfig_deviceConfigSep_successConnect'))
        } else {
          this.alertDialog.show(this.$t('sm.deviceConfig_deviceConfigSep_failed'))
        }
      } catch (e) {
        this.$log('[Device Context Menu GUI Error (checkConnect)]', e)
      }
    }
  }
}
